import { useTranslation } from "react-i18next";

import {
  PanelStackContext,
  useRoomScheduleStatusForRequest,
  useRoomSensorStatusForRequest,
  useRoomsRequest,
} from "@lib/logic";
import { getFloorHasMap, getIsMeetingsCalendarActive, type MeetingRoom, useAppSelector } from "@lib/store";
import { useRequiredContext } from "@lib/utils";
import { MeetingRoomActivities } from "src/components/organisms/meetingRoom/MeetingRoomActivities/MeetingRoomActivities";
import { MeetingRoomEquipment } from "src/components/organisms/meetingRoom/MeetingRoomEquipment/MeetingRoomEquipment";
import { MeetingRoomImage } from "src/components/organisms/meetingRoom/MeetingRoomImage/MeetingRoomImage";
import { MeetingRoomInfo } from "src/components/organisms/meetingRoom/MeetingRoomInfo/MeetingRoomInfo";
import { MeetingRoomData } from "src/components/organisms/meetingRoom/MeetingRoomData";

import { LocationBasedServices } from "../../../../organisms/LocationBasedServices";
import { useHaNLayoutMode } from "../../state/layout/useHaNLayoutMode";
import { ControlsSection } from "../controls/ControlsSection";
import { HaNDetailsPage } from "../HaNDetailsPage";
import { HaNMapPreview } from "../HaNMapPreview";
import { RoomDetailsFooter } from "./RoomDetailsFooter";

// The footer books the room, so it's only there when the room has a calendar and the user's calendar is connected
export const RoomInfoPage = ({ room }: { room: MeetingRoom }) => {
  const { t } = useTranslation();
  const { push } = useRequiredContext(PanelStackContext);
  const request = useRoomsRequest();
  const layoutMode = useHaNLayoutMode();
  const scheduleStatus = useRoomScheduleStatusForRequest(request, room.id, room.email);
  const sensorStatus = useRoomSensorStatusForRequest(request, room.id);
  const floorHasMap = useAppSelector(getFloorHasMap(room.floorId));
  const isMeetingsCalendarActive = useAppSelector(getIsMeetingsCalendarActive);

  const isBookable = !!room.email && isMeetingsCalendarActive;

  return (
    <HaNDetailsPage
      title={t("screen:MeetingRoomDetails")}
      footer={isBookable && <RoomDetailsFooter room={room} />}>
      <section className="flex flex-col gap-8 px-5 py-6">
        <MeetingRoomInfo
          roomName={room.displayName}
          buildingName={room.buildingName}
          floorName={room.floorName}
          capacity={room.capacity}
        />

        <MeetingRoomImage
          displayName={room.displayName}
          email={room.email}
          roomPictureHash={room.roomPictureHash}
          buildingId={room.buildingId}
        />

        <MeetingRoomData
          sensorStatus={sensorStatus}
          scheduleStatus={scheduleStatus}
          onLearnMorePress={() => push("sensorInfo")}
        />

        <MeetingRoomActivities activities={room.activities} />

        <MeetingRoomEquipment equipment={room.equipment} />

        <ControlsSection nodeId={room.id} />

        {floorHasMap && layoutMode === "mobile" && (
          <HaNMapPreview
            buildingId={room.buildingId}
            floorId={room.floorId}
            nodeId={room.id}
          />
        )}

        <LocationBasedServices
          buildingId={room.buildingId}
          locationType="MeetingRoom"
          locationName={room.displayName}
        />
      </section>
    </HaNDetailsPage>
  );
};
